import { Grid } from '../../core/Grid.js';
import {
    Position,
    positionKey,
    parsePositionKey
} from '../../core/GridGeometry.js';
import { DelugeGame } from './DelugeGame.js';
import {
    SectionTag,
    Basin,
    Rational,
    DelugeSliceState,
    sectionKey,
    parseSectionKey,
    getSections,
    getSectionSize,
    getSectionContaining,
    sectionContains,
    getExternalNeighbors,
    getHorizontalNeighbors,
    getNeighborSection,
    cellKey,
    reduceRational,
    addRationals,
    subtractRationals,
    multiplyRationals,
    divideRational,
    compareRationals,
    rationalToNumber
} from './DelugeUtils.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

// Encode a parent as "sliceId:sectionKey" (or null for faucet)
function encodeParent(sliceId: string, sk: string): string {
    return `${sliceId}:${sk}`;
}

function decodeParent(encoded: string): { sliceId: string; sectionKey: string } {
    const colonIdx = encoded.lastIndexOf(':');
    return {
        sliceId: encoded.slice(0, colonIdx),
        sectionKey: encoded.slice(colonIdx + 1)
    };
}

// Build a slice ID from an ancestry array
function ancestryToSliceId(ancestry: string[]): string {
    return ancestry.join('|');
}

export class DelugeVortex extends DelugeGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    constructor(grid: Grid) {
        super(grid);
    }

    // ── Vortex Data ───────────────────────────────────────────────

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        // Recompute the flow simulation: a Hub gate deletion can prune an active
        // pair here, and checkWin/render read the cached fill times.
        this.gm.setVortexData(gatePairs, allGates, () => {
            this.recalculate();
        });
    }

    // ── Gate Queries ──────────────────────────────────────────────

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, pairedPos) => {
            if (this.getGrid().hasDigitAt(p)) return false;
            if (this.getGrid().hasDigitAt(pairedPos)) return false;
            // Gates cannot be on cells with walls
            if (this.getWallType(p.row, p.col) !== null) return false;
            if (this.getWallType(pairedPos.row, pairedPos.col) !== null) return false;
            return true;
        });
    }

    toggleVortexGate(pos: Position): boolean {
        return this.gm.toggleVortexGate(pos, this.canActivateGate(pos), () => {
            this.recalculate();
        });
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // Override hook: block wall placement on active gate cells
    protected override isGateCellAt(row: number, col: number): boolean {
        return this.gm.raw.has(positionKey({ row, col }));
    }

    // ── Vortex Flow Algorithm ─────────────────────────────────────

    private hasActiveGates(): boolean {
        return this.gm !== undefined && this.gm.hasActiveGates();
    }

    // Check if a section's cell has a passable gate (cell not in slice's ancestry)
    private getPassableGate(sk: string, ancestry: string[]): string | null {
        const { row, col } = parseSectionKey(sk);
        const ck = cellKey(row, col);
        const pairedPosKey = this.gm.raw.get(ck);
        if (!pairedPosKey) return null;
        // Gate cannot be traversed twice in the same direction
        if (ancestry.includes(ck)) return null;
        return pairedPosKey;
    }

    // Get entry section key for a gate destination cell (the section containing 'u')
    private getGateDestinationSection(pairedPosKey: string): string {
        const pos = parsePositionKey(pairedPosKey);
        const wallType = this.getWallType(pos.row, pos.col);
        // Gate cells should have no walls, so this returns 'uldr'
        const section = getSectionContaining('u', wallType);
        return sectionKey(pos.row, pos.col, section);
    }

    // Get or create a slice for the given ancestry
    private getOrCreateSlice(slices: Map<string, DelugeSliceState>, ancestry: string[]): DelugeSliceState {
        const id = ancestryToSliceId(ancestry);
        let slice = slices.get(id);
        if (!slice) {
            slice = { ancestry: [...ancestry], flowState: new Map() };
            slices.set(id, slice);
        }
        return slice;
    }

    // Tag a section in a specific slice if not already tagged
    private tagSectionInSlice(
        sk: string,
        tag: SectionTag,
        slice: DelugeSliceState,
        toResolve: Array<{ sliceId: string; sectionKey: string }>
    ): void {
        if (!slice.flowState.has(sk)) {
            slice.flowState.set(sk, tag);
            toResolve.push({ sliceId: ancestryToSliceId(slice.ancestry), sectionKey: sk });
        }
    }

    // Resolve a drop in the vortex flow algorithm
    private resolveVortexDrop(
        sk: string,
        slice: DelugeSliceState,
        slices: Map<string, DelugeSliceState>,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        toResolve: Array<{ sliceId: string; sectionKey: string }>,
        dropParents: Set<string>
    ): void {
        const tag = slice.flowState.get(sk);
        if (!tag || tag.type !== 'drop' || tag.resolved) return;

        // Record this drop's parent eagerly — the drop may be retagged to crash
        if (tag.parent) dropParents.add(tag.parent);

        const { row, col, sectionId } = parseSectionKey(sk);
        const sliceId = ancestryToSliceId(slice.ancestry);
        const ck = cellKey(row, col);

        // Check for gate: neither A nor Z must be in ancestry
        const pairedPosKey = this.getPassableGate(sk, slice.ancestry);
        if (pairedPosKey !== null && !slice.ancestry.includes(pairedPosKey)) {
            // Mark A in A-slice resolved
            tag.resolved = true;
            // Create Z-slice with ancestry N+A
            const zAncestry = [...slice.ancestry, ck];
            const zSlice = this.getOrCreateSlice(slices, zAncestry);
            // Tag Z's entry section as drop in Z-slice
            const destSk = this.getGateDestinationSection(pairedPosKey);
            const dropTag: SectionTag = { type: 'drop', parent: encodeParent(sliceId, sk), rate: tag.rate, resolved: false };
            this.tagSectionInSlice(destSk, dropTag, zSlice, toResolve);
            return;
        }

        // If filled or partially filled, retag as crash
        if (filled.has(sk) || waterLevel.has(sk)) {
            const crashTag: SectionTag = { type: 'crash', parent: tag.parent, rate: tag.rate!, resolved: false };
            slice.flowState.set(sk, crashTag);
            this.resolveVortexCrash(sk, slice, slices, filled, waterLevel, toResolve);
            return;
        }

        // Let T be the section below S
        const belowKey = sectionContains(sectionId, 'd')
            ? getNeighborSection(row, col, 'd', (r, c) => this.getWallType(r, c), this.grid.size)
            : null;

        if (belowKey === null) {
            const crashTag: SectionTag = { type: 'crash', parent: tag.parent, rate: tag.rate!, resolved: false };
            slice.flowState.set(sk, crashTag);
            this.resolveVortexCrash(sk, slice, slices, filled, waterLevel, toResolve);
            return;
        }

        if (filled.has(belowKey)) {
            const crashTag: SectionTag = { type: 'crash', parent: tag.parent, rate: tag.rate!, resolved: false };
            slice.flowState.set(sk, crashTag);
            this.resolveVortexCrash(sk, slice, slices, filled, waterLevel, toResolve);
            return;
        }

        // T exists and is empty - drop continues
        tag.resolved = true;
        const dropTag: SectionTag = { type: 'drop', parent: encodeParent(sliceId, sk), rate: tag.rate, resolved: false };
        this.tagSectionInSlice(belowKey, dropTag, slice, toResolve);
    }

    // Resolve a crash in the vortex flow algorithm
    private resolveVortexCrash(
        sk: string,
        slice: DelugeSliceState,
        slices: Map<string, DelugeSliceState>,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        toResolve: Array<{ sliceId: string; sectionKey: string }>
    ): void {
        const tag = slice.flowState.get(sk);
        if (!tag || tag.type !== 'crash' || tag.resolved) return;

        const { row, col, sectionId } = parseSectionKey(sk);
        const sliceId = ancestryToSliceId(slice.ancestry);
        const ck = cellKey(row, col);
        tag.resolved = true;

        const getWallTypeFn = (r: number, c: number) => this.getWallType(r, c);

        // If filled or partially filled, tag all connected non-empty sections as spread
        if (filled.has(sk) || waterLevel.has(sk)) {
            const neighbors = getExternalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const nk of neighbors) {
                if (waterLevel.has(nk) || filled.has(nk)) {
                    this.tagSectionInSlice(nk, { type: 'spread', parent: encodeParent(sliceId, sk), via: sk, resolved: false }, slice, toResolve);
                }
            }
        } else {
            // Check below
            const belowKey = sectionContains(sectionId, 'd')
                ? getNeighborSection(row, col, 'd', getWallTypeFn, this.grid.size)
                : null;

            // Tag horizontal neighbors as spread
            const hNeighbors = getHorizontalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const nk of hNeighbors) {
                if (!filled.has(nk)) {
                    this.tagSectionInSlice(nk, { type: 'spread', parent: encodeParent(sliceId, sk), via: sk, resolved: false }, slice, toResolve);
                }
            }

            // If below exists and is filled, tag it as spread
            if (belowKey && filled.has(belowKey)) {
                this.tagSectionInSlice(belowKey, { type: 'spread', parent: encodeParent(sliceId, sk), via: sk, resolved: false }, slice, toResolve);
            }
        }

        // Gate check (applies to all crash cases)
        const pairedPosKey = this.getPassableGate(sk, slice.ancestry);
        if (pairedPosKey !== null) {
            const zAncestry = [...slice.ancestry, ck];
            const zSlice = this.getOrCreateSlice(slices, zAncestry);
            const destSk = this.getGateDestinationSection(pairedPosKey);
            this.tagSectionInSlice(destSk, { type: 'spread', parent: encodeParent(sliceId, sk), resolved: false }, zSlice, toResolve);
        }
    }

    // Resolve a spread in the vortex flow algorithm
    private resolveVortexSpread(
        sk: string,
        slice: DelugeSliceState,
        slices: Map<string, DelugeSliceState>,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        toResolve: Array<{ sliceId: string; sectionKey: string }>,
        waitingDrops: Array<{ sliceId: string; sectionKey: string }>
    ): void {
        const tag = slice.flowState.get(sk);
        if (!tag || tag.type !== 'spread' || tag.resolved) return;

        const { row, col, sectionId } = parseSectionKey(sk);
        const parentCrash = tag.parent;
        const sliceId = ancestryToSliceId(slice.ancestry);
        const ck = cellKey(row, col);
        tag.resolved = true;

        const getWallTypeFn = (r: number, c: number) => this.getWallType(r, c);

        // If filled, tag all connected sections as spread
        if (filled.has(sk)) {
            const neighbors = getExternalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const nk of neighbors) {
                this.tagSectionInSlice(nk, { type: 'spread', parent: parentCrash, via: sk, resolved: false }, slice, toResolve);
            }
            // Gate check
            const pairedPosKey = this.getPassableGate(sk, slice.ancestry);
            if (pairedPosKey !== null) {
                const zAncestry = [...slice.ancestry, ck];
                const zSlice = this.getOrCreateSlice(slices, zAncestry);
                const destSk = this.getGateDestinationSection(pairedPosKey);
                // Re-encode parent: keep the crash key but with A-slice context
                const reEncodedParent = parentCrash !== null
                    ? encodeParent(sliceId, decodeParent(parentCrash).sectionKey)
                    : null;
                this.tagSectionInSlice(destSk, { type: 'spread', parent: reEncodedParent, resolved: false }, zSlice, toResolve);
            }
            return;
        }

        // If partially filled, tag connected non-empty sections as spread
        if (waterLevel.has(sk)) {
            const neighbors = getExternalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const nk of neighbors) {
                if (waterLevel.has(nk) || filled.has(nk)) {
                    this.tagSectionInSlice(nk, { type: 'spread', parent: parentCrash, via: sk, resolved: false }, slice, toResolve);
                }
            }
            // Gate check
            const pairedPosKey = this.getPassableGate(sk, slice.ancestry);
            if (pairedPosKey !== null) {
                const zAncestry = [...slice.ancestry, ck];
                const zSlice = this.getOrCreateSlice(slices, zAncestry);
                const destSk = this.getGateDestinationSection(pairedPosKey);
                const reEncodedParent = parentCrash !== null
                    ? encodeParent(sliceId, decodeParent(parentCrash).sectionKey)
                    : null;
                this.tagSectionInSlice(destSk, { type: 'spread', parent: reEncodedParent, resolved: false }, zSlice, toResolve);
            }
            return;
        }

        // Section is empty
        // Check for passable gate — retag as drop (waiting list)
        const pairedPosKey = this.getPassableGate(sk, slice.ancestry);
        if (pairedPosKey !== null) {
            const dropTag: SectionTag = { type: 'drop', parent: parentCrash, rate: null, resolved: false };
            slice.flowState.set(sk, dropTag);
            waitingDrops.push({ sliceId, sectionKey: sk });
            return;
        }

        const belowKey = sectionContains(sectionId, 'd')
            ? getNeighborSection(row, col, 'd', getWallTypeFn, this.grid.size)
            : null;

        const canDrop = belowKey !== null;

        if (!canDrop) {
            // No section below - spread horizontally only
            const hNeighbors = getHorizontalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const nk of hNeighbors) {
                if (!filled.has(nk)) {
                    this.tagSectionInSlice(nk, { type: 'spread', parent: parentCrash, via: sk, resolved: false }, slice, toResolve);
                }
            }
        } else if (filled.has(belowKey)) {
            // Below is filled - spread to below AND horizontal neighbors
            this.tagSectionInSlice(belowKey, { type: 'spread', parent: parentCrash, via: sk, resolved: false }, slice, toResolve);
            const hNeighbors = getHorizontalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const nk of hNeighbors) {
                if (!filled.has(nk)) {
                    this.tagSectionInSlice(nk, { type: 'spread', parent: parentCrash, via: sk, resolved: false }, slice, toResolve);
                }
            }
        } else {
            // Below is empty - retag as drop. The side is the step the water took to
            // get here, not the crash it came from; `via` is a plain section key in
            // this slice, so it needs no decoding.
            this.recordDropHint(sk, tag.via ?? null);
            const dropTag: SectionTag = { type: 'drop', parent: parentCrash, rate: null, resolved: false };
            slice.flowState.set(sk, dropTag);
            waitingDrops.push({ sliceId, sectionKey: sk });
        }
    }

    // Build flow state with multiple slices for vortex
    private buildVortexFlowState(
        faucetCol: number,
        filled: Set<string>,
        waterLevel: Map<string, Rational>
    ): { slices: Map<string, DelugeSliceState>; basins: Basin[] } {
        const slices = new Map<string, DelugeSliceState>();

        // Create initial slice with empty ancestry
        const initialSlice: DelugeSliceState = { ancestry: [], flowState: new Map() };
        slices.set('', initialSlice);

        const toResolve: Array<{ sliceId: string; sectionKey: string }> = [];
        const waitingDrops: Array<{ sliceId: string; sectionKey: string }> = [];
        // Track drop parents eagerly — drops may be retagged to crash, losing the link
        const dropParents = new Set<string>();

        // Start with section below faucet
        const startWallType = this.getWallType(0, faucetCol);
        const startSection = getSectionContaining('u', startWallType);
        const startKey = sectionKey(0, faucetCol, startSection);
        const startTag: SectionTag = { type: 'drop', parent: null, rate: { num: 1, denom: 1 }, resolved: false };
        initialSlice.flowState.set(startKey, startTag);
        toResolve.push({ sliceId: '', sectionKey: startKey });

        // Resolve all sections
        while (toResolve.length > 0) {
            const { sliceId, sectionKey: sk } = toResolve.shift()!;
            const slice = slices.get(sliceId);
            if (!slice) continue;

            const tag = slice.flowState.get(sk);
            if (!tag || tag.resolved) continue;

            if (tag.type === 'drop') {
                this.resolveVortexDrop(sk, slice, slices, filled, waterLevel, toResolve, dropParents);
            } else if (tag.type === 'crash') {
                this.resolveVortexCrash(sk, slice, slices, filled, waterLevel, toResolve);
            } else if (tag.type === 'spread') {
                this.resolveVortexSpread(sk, slice, slices, filled, waterLevel, toResolve, waitingDrops);
            }
        }

        // Process waiting drops iteratively until none remain
        while (waitingDrops.length > 0) {
            // Group by parent (encoded as sliceId:sectionKey)
            const byParent = new Map<string, Array<{ sliceId: string; sectionKey: string }>>();
            const currentWaiting = [...waitingDrops];
            waitingDrops.length = 0;

            for (const entry of currentWaiting) {
                const slice = slices.get(entry.sliceId);
                if (!slice) continue;
                const tag = slice.flowState.get(entry.sectionKey) as SectionTag & { type: 'drop' };
                const parent = tag.parent!;
                if (!byParent.has(parent)) byParent.set(parent, []);
                byParent.get(parent)!.push(entry);
            }

            // Assign rates and resolve
            for (const [parentEncoded, children] of byParent) {
                const { sliceId: parentSliceId, sectionKey: parentSk } = decodeParent(parentEncoded);
                const parentSlice = slices.get(parentSliceId);
                if (!parentSlice) continue;

                const parentTag = parentSlice.flowState.get(parentSk);
                if (!parentTag || parentTag.type !== 'crash') continue;

                const parentRate = parentTag.rate;
                const childRate = divideRational(parentRate, children.length);

                for (const child of children) {
                    const childSlice = slices.get(child.sliceId);
                    if (!childSlice) continue;
                    const tag = childSlice.flowState.get(child.sectionKey) as SectionTag & { type: 'drop' };
                    tag.rate = childRate;
                    toResolve.push(child);
                }
            }

            // Resolve the waiting drops (may add more to waitingDrops)
            while (toResolve.length > 0) {
                const { sliceId, sectionKey: sk } = toResolve.shift()!;
                const slice = slices.get(sliceId);
                if (!slice) continue;

                const tag = slice.flowState.get(sk);
                if (!tag || tag.resolved) continue;

                if (tag.type === 'drop') {
                    this.resolveVortexDrop(sk, slice, slices, filled, waterLevel, toResolve, dropParents);
                } else if (tag.type === 'crash') {
                    this.resolveVortexCrash(sk, slice, slices, filled, waterLevel, toResolve);
                } else if (tag.type === 'spread') {
                    this.resolveVortexSpread(sk, slice, slices, filled, waterLevel, toResolve, waitingDrops);
                }
            }
        }

        // Find terminal crashes across all slices and build basins
        // dropParents was collected eagerly during resolution (drops may be retagged to crash)

        // Collect terminal crashes and their spread children
        const rawBasins: Array<{ crashParent: string; sections: Set<string>; rate: Rational }> = [];

        for (const [sliceId, slice] of slices) {
            for (const [sk, tag] of slice.flowState) {
                if (tag.type === 'crash') {
                    const crashKey = encodeParent(sliceId, sk);
                    if (!dropParents.has(crashKey)) {
                        // Terminal crash - collect basin sections across all slices
                        const basinSections = new Set<string>();
                        if (!filled.has(sk)) basinSections.add(sk);
                        for (const [, anySlice] of slices) {
                            for (const [ssk, stag] of anySlice.flowState) {
                                if (stag.type === 'spread' && stag.parent === crashKey && !filled.has(ssk)) {
                                    basinSections.add(ssk);
                                }
                            }
                        }
                        rawBasins.push({ crashParent: crashKey, sections: basinSections, rate: tag.rate });
                    }
                }
            }
        }

        const basins: Basin[] = rawBasins.map(rb => ({
            sections: [...rb.sections],
            rate: rb.rate
        }));

        return { slices, basins };
    }

    protected override simulate(): void {
        // If no active gates, delegate to base class
        if (!this.hasActiveGates()) {
            super.simulate();
            return;
        }

        this.sectionFillTime.clear();
        this.reachableSections.clear();
        this.dropHints.clear();
        this.debug.clear();

        // Reachability first, so a walled-off area is still reported and shaded on a
        // board whose walls are also disconnected — see DelugeGame.simulate.
        this.updateReachability();

        if (!this.validateWallConnectivity()) return;
        if (this.hasUnreachableSections) return;

        const filled = new Set<string>();
        const waterLevel = new Map<string, Rational>();
        let currentTime: Rational = { num: 0, denom: 1 };

        // Total sections in grid
        let totalSections = 0;
        for (let row = 0; row < this.grid.size; row++) {
            for (let col = 0; col < this.grid.size; col++) {
                const wallType = this.getWallType(row, col);
                totalSections += getSections(wallType).length;
            }
        }

        const maxIterations = totalSections * 2;
        let iterations = 0;
        let debugTimestampIndex = 0;

        while (iterations++ < maxIterations && rationalToNumber(currentTime) < 400) {
            const { slices, basins } = this.buildVortexFlowState(this.faucetCol, filled, waterLevel);

            // Capture vortex snapshots at debug timestamps
            debugTimestampIndex = this.debug.maybeCaptureVortexSnapshot(
                rationalToNumber(currentTime),
                debugTimestampIndex,
                filled,
                waterLevel,
                slices,
                basins
            );

            if (basins.length === 0) {
                break;
            }

            // Aggregate fill rates per section across basins
            // Each basin contributes its rate / totalQuadrants to each of its sections' quadrants
            // A section can appear in multiple basins (from different slices)
            const sectionRateContributions = new Map<string, Rational>();
            const sectionToBasinIndices = new Map<string, number[]>();

            for (let i = 0; i < basins.length; i++) {
                const basin = basins[i];
                // Total quadrants in this basin
                let totalQuadrants = 0;
                for (const sk of basin.sections) {
                    const { sectionId } = parseSectionKey(sk);
                    totalQuadrants += getSectionSize(sectionId);
                }
                const perQuadrantRate = divideRational(basin.rate, totalQuadrants);

                for (const sk of basin.sections) {
                    const existing = sectionRateContributions.get(sk);
                    if (existing) {
                        sectionRateContributions.set(sk, addRationals(existing, perQuadrantRate));
                    } else {
                        sectionRateContributions.set(sk, perQuadrantRate);
                    }
                    if (!sectionToBasinIndices.has(sk)) sectionToBasinIndices.set(sk, []);
                    sectionToBasinIndices.get(sk)!.push(i);
                }
            }

            // Calculate fill time for each unique section
            let earliestTime: Rational | null = null;

            const sectionFillTimes: Array<{ sk: string; fillTime: Rational; aggregatedRate: Rational }> = [];

            for (const [sk, rate] of sectionRateContributions) {
                const currentLevel = waterLevel.get(sk) ?? { num: 0, denom: 1 };
                const remaining = subtractRationals({ num: 1, denom: 1 }, currentLevel);

                if (compareRationals(remaining, { num: 0, denom: 1 }) <= 0) continue;

                // Time to fill = remaining / rate (rate is per-quadrant)
                const timeToFill: Rational = reduceRational({
                    num: remaining.num * rate.denom,
                    denom: remaining.denom * rate.num
                });
                const fillTime = addRationals(currentTime, timeToFill);

                sectionFillTimes.push({ sk, fillTime, aggregatedRate: rate });

                if (earliestTime === null || compareRationals(fillTime, earliestTime) < 0) {
                    earliestTime = fillTime;
                }
            }

            if (earliestTime === null) break;

            // Advance time and update water levels for all sections
            const elapsed = subtractRationals(earliestTime, currentTime);
            for (const { sk, aggregatedRate } of sectionFillTimes) {
                const added = multiplyRationals(elapsed, aggregatedRate);
                const current = waterLevel.get(sk) ?? { num: 0, denom: 1 };
                waterLevel.set(sk, addRationals(current, added));
            }

            // Mark sections that complete at the earliest time
            const fillTimeNum = Math.round(rationalToNumber(earliestTime) * 10) / 10;
            for (const { sk, fillTime } of sectionFillTimes) {
                if (compareRationals(fillTime, earliestTime!) === 0) {
                    this.sectionFillTime.set(sk, fillTimeNum);
                    filled.add(sk);
                }
            }

            currentTime = earliestTime;
        }

        // Count expected sections
        let expectedSections = 0;
        for (let row = 0; row < this.grid.size; row++) {
            for (let col = 0; col < this.grid.size; col++) {
                const wallType = this.getWallType(row, col);
                expectedSections += getSections(wallType).length;
            }
        }

        // Check if flow algorithm filled all sections
        if (this.sectionFillTime.size < expectedSections) {
            return;
        }
    }

    /** Reachability has to follow gates here, not just open section borders. Mirrors
     *  {@link simulate}'s delegation: with no active gates there's nothing extra to
     *  follow, and the base constructor reaches this before the gate state exists. */
    protected override reachableFrom(faucetCol: number): Set<string> {
        if (!this.hasActiveGates()) return super.reachableFrom(faucetCol);
        return this.vortexFloodFillReachable(faucetCol);
    }

    // Flood-fill reachability that includes gate teleportation (section-based)
    private vortexFloodFillReachable(faucetCol: number): Set<string> {
        const reachable = new Set<string>();
        const wallType = this.getWallType(0, faucetCol);
        const startSection = getSectionContaining('u', wallType);
        const startKey = sectionKey(0, faucetCol, startSection);
        const queue: string[] = [startKey];

        const getWallTypeFn = (r: number, c: number) => this.getWallType(r, c);

        while (queue.length > 0) {
            const sk = queue.shift()!;
            if (reachable.has(sk)) continue;
            reachable.add(sk);

            const { row, col, sectionId } = parseSectionKey(sk);

            // Normal neighbors
            const neighbors = getExternalNeighbors(row, col, sectionId, getWallTypeFn, this.grid.size);
            for (const nk of neighbors) {
                if (!reachable.has(nk)) queue.push(nk);
            }

            // Gate teleportation: if this section's cell has an active gate,
            // add the destination cell's sections
            const ck = cellKey(row, col);
            const pairedPosKey = this.gm.raw.get(ck);
            if (pairedPosKey) {
                const pairedPos = parsePositionKey(pairedPosKey);
                const pairedWallType = this.getWallType(pairedPos.row, pairedPos.col);
                const pairedSections = getSections(pairedWallType);
                for (const sec of pairedSections) {
                    const psk = sectionKey(pairedPos.row, pairedPos.col, sec);
                    if (!reachable.has(psk)) queue.push(psk);
                }
            }
        }

        return reachable;
    }

    // ── Debug ──────────────────────────────────────────────────────

    logVortexDebugSnapshots(): void {
        this.debug.logVortexSnapshots();
    }

    // ── State Management ──────────────────────────────────────────

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // ── Serialization ─────────────────────────────────────────────

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        if (!serialized) {
            super.deserialize('');
            return;
        }

        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
        this.recalculate();
    }
}
